import { useSeo } from '../lib/seo';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ArrowUpRight, Calendar, ChevronDown, Cloud, CloudRain, Compass, Footprints, MapPin, Snowflake, Sparkles, Sun } from 'lucide-react';
import api from '../lib/api';
import { track } from '../lib/tracker';
import { mergeHeadlines } from '../lib/heroHeadlines';
import AttractionCard from '../components/AttractionCard';
import NewsletterSignup from '../components/NewsletterSignup';
import MobileSnapCarousel from '../components/MobileSnapCarousel';
import ScrollStrip from '../components/ScrollStrip';
import { pickDaily } from '../lib/daily';
import SmartImage from '../components/SmartImage';
import { eventPlaceholder } from '../lib/eventPlaceholder';
import { isOpenToday, isEveningMode, effectiveDate, effectiveDayLabel } from '../lib/openingHours';
import { getSchoolHolidays, findCurrentHoliday } from '../lib/schoolHolidays';
import { AboutTeaser } from './AboutPage';

// Human-readable "X minutes ago" for the freshness pill on the weekend strip.
function timeAgo(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return 'Updated just now';
  if (mins < 60) return `Updated ${mins} min${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `Updated ${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.round(hrs / 24);
  return `Updated ${days} day${days === 1 ? '' : 's'} ago`;
}

const WEATHER_ICON = { sunny: Sun, rainy: CloudRain, cloudy: Cloud, snowy: Snowflake };

// Curated rotation of distinctive Leicestershire imagery, tagged by best season.
// The hero adapts to the visitor's local time-of-day and the current season so
// the homepage feels alive rather than static.
const HERO_LIBRARY = [
  { src: '/api/uploads/migrated/b8418e427029c84c.jpg',  alt: 'Belvoir Castle',                       seasons: ['autumn', 'winter', 'spring'] },
  { src: '/api/uploads/migrated/9b7e6c1cfea3b2e7.jpg',  alt: 'Foxton Locks',                         seasons: ['spring', 'summer'] },
  { src: '/api/uploads/migrated/2d1deb1707959671.jpg',  alt: 'Great Central Railway',                seasons: ['autumn', 'winter'] },
  { src: '/api/uploads/migrated/f7f7922bdfbbb388.jpg',  alt: 'Twycross Zoo',                         seasons: ['spring', 'summer', 'autumn'] },
  { src: '/api/uploads/migrated/f7f9ad8c68577073.png',  alt: '1620s House & Garden',                 seasons: ['spring', 'summer'] },
  { src: '/api/uploads/migrated/2e2aec0755a13dfe.jpeg', alt: 'Conkers — the National Forest',       seasons: ['autumn', 'spring', 'summer'] },
  { src: '/api/uploads/migrated/63e00d40db19848a.jpeg', alt: 'Bosworth Battlefield Heritage Centre', seasons: ['autumn', 'winter', 'spring'] },
  { src: '/api/uploads/migrated/d2c8a9b59bdbfc0a.jpeg', alt: 'National Forest E-Bike Holidays',      seasons: ['summer', 'spring', 'autumn'] },
];

function dayOfYear(d = new Date()) {
  const start = new Date(d.getFullYear(), 0, 0);
  const diff = d - start + (start.getTimezoneOffset() - d.getTimezoneOffset()) * 60 * 1000;
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

// UK meteorological seasons (Mar–May spring, Jun–Aug summer, Sep–Nov autumn, Dec–Feb winter)
function seasonContext(d = new Date()) {
  const m = d.getMonth(); // 0 Jan … 11 Dec
  if (m >= 2 && m <= 4) return { key: 'spring', label: 'A Leicestershire spring', sub: 'Bluebells, lambs, longer afternoons.' };
  if (m >= 5 && m <= 7) return { key: 'summer', label: 'Long Leicestershire summers', sub: 'Canalside pubs, garden trails, late golden light.' };
  if (m >= 8 && m <= 10) return { key: 'autumn', label: 'An autumn worth showing up for', sub: 'National Forest colour, crisp pub lunches, harvest fairs.' };
  return { key: 'winter', label: 'A proper Leicestershire winter', sub: 'Frosty fields, cosy pubs, festive markets.' };
}

// Time-of-day greeting + CSS overlay tint. The overlay sits above the hero
// image at low opacity so the photo still reads through.
function timeContext(d = new Date()) {
  const h = d.getHours();
  if (h >= 5 && h < 11)  return { key: 'morning', greeting: 'Good morning', overlay: 'linear-gradient(180deg, rgba(255, 196, 130, 0.18) 0%, rgba(255, 138, 100, 0.10) 50%, transparent 100%)' };
  if (h >= 11 && h < 17) return { key: 'day',     greeting: 'Hello',        overlay: 'linear-gradient(180deg, rgba(255, 255, 255, 0.04) 0%, transparent 60%)' };
  if (h >= 17 && h < 20) return { key: 'evening', greeting: 'Good evening', overlay: 'linear-gradient(180deg, rgba(255, 122, 60, 0.20) 0%, rgba(140, 60, 100, 0.18) 60%, rgba(20, 24, 50, 0.10) 100%)' };
  return                       { key: 'night',   greeting: 'Good evening', overlay: 'linear-gradient(180deg, rgba(20, 30, 70, 0.35) 0%, rgba(15, 20, 50, 0.30) 100%)' };
}

function pickHeroImage(d = new Date()) {
  const season = seasonContext(d).key;
  const pool = HERO_LIBRARY.filter((h) => h.seasons.includes(season));
  const lib = pool.length ? pool : HERO_LIBRARY;
  const chosen = lib[dayOfYear(d) % lib.length];
  return `${process.env.REACT_APP_BACKEND_URL}${chosen.src}`;
}

// Day-of-week aware messaging so the hero never says "Made for weekends" on a Monday.
function dayContext(d = new Date()) {
  const dow = d.getDay(); // 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat
  if (dow === 5 || dow === 6 || dow === 0) return { tagline: 'Made for weekends', weekend: true };
  if (dow === 4) return { tagline: 'Made for the weekend ahead', weekend: false };
  return { tagline: 'Made for midweek escapes', weekend: false };
}

function useFetch(path, deps = []) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!path) return undefined;
    let alive = true;
    api.get(path).then((r) => alive && setData(r.data)).catch(() => alive && setData({}));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return data;
}

export default function HomePage() {
  useSeo(
    'Visit Leicestershire — Days Out, Venues & Events | GoLeicestershire',
    "Visiting Leicester or Leicestershire? Your simple guide to 85+ venues and attractions, what's-on event listings, market towns, family days out and short breaks across the city and county."
  );
  const [weather, setWeather] = useState(null);
  const [county, setCounty] = useState(null);
  const [heroOverride, setHeroOverride] = useState(null);
  const [heroFavs, setHeroFavs] = useState([]);
  const [overrideIdx, setOverrideIdx] = useState(null);

  useEffect(() => {
    api.get('/weather').then((r) => setWeather(r.data));
    api.get('/weather/county').then((r) => setCounty(r.data));
    api.get('/site-settings/homepage_hero')
      .then((r) => setHeroOverride(r.data.value?.url ? r.data.value : null))
      .catch(() => {});
    api.get('/site-settings/hero_favourites')
      .then((r) => setHeroFavs(r.data.value?.items || []))
      .catch(() => {});
  }, []);

  // Click to cycle: bump to next location in the all_locations array
  function cycleLocation() {
    if (!county || !county.locations || county.locations.length === 0) return;
    const cur = overrideIdx == null ? county.locations.findIndex((l) => l.location === weather?.location) : overrideIdx;
    const next = (cur + 1) % county.locations.length;
    setOverrideIdx(next);
    setWeather(county.locations[next]);
  }

  // Featured attractions = a county-wide selection (no town bias). We used to
  // bias these toward the visitor's geolocated town, but that made the
  // homepage of a county-wide DMO site feel like a microsite for whichever
  // town the user happened to be browsing from (e.g. "Beyond Market
  // Harborough", "More from Market Harborough"). Now everyone sees the same
  // Leicestershire-wide standout days out.
  const [featured, setFeatured] = useState(null);
  useEffect(() => {
    api.get('/attractions?limit=8').then((r) => setFeatured(r.data));
  }, []);
  const heritage = useFetch('/attractions?category=heritage&limit=4');
  const outdoors = useFetch('/attractions?category=outdoors&limit=4');
  // Larger pools for the daily-rotating "Today's pick" tile. We pick ONE
  // venue per day from the matching weather pool so the tile genuinely refreshes.
  const outdoorPool = useFetch('/attractions?outdoor=true&limit=40');
  const indoorPool = useFetch('/attractions?indoor=true&limit=40');
  const towns = useFetch('/towns');
  const heroCopy = useFetch('/site-settings/hero_headlines');
  const weekend = useFetch('/events/this-weekend');
  const packages = useFetch('/packages');
  const digest = useFetch('/digest/latest');
  const members = useFetch('/members?limit=40');
  // School-holiday rail — covers every holiday (half-terms, Christmas, Easter,
  // summer); backend keeps dates fresh year-on-year
  const [schoolHoliday, setSchoolHoliday] = useState(null);
  useEffect(() => { getSchoolHolidays().then((rs) => setSchoolHoliday(findCurrentHoliday(rs))); }, []);
  const holidayIdeas = useFetch(schoolHoliday ? '/attractions?family=true&limit=24' : null, [schoolHoliday?.from]);

  const condition = weather?.condition || 'cloudy';

  // Evening-mode awareness for the "Today's pick" tile. After 18:00 UK time
  // we flip to "Tomorrow's pick", look up tomorrow's forecast from the
  // weather payload, and pass tomorrow's date to `isOpenToday()` so we don't
  // recommend a venue closed tomorrow (e.g. Sunday-closing pubs the night
  // before).
  const evening = isEveningMode();
  const targetDate = effectiveDate();
  const dayLabel = effectiveDayLabel();
  const tomorrowIso = targetDate.toISOString().slice(0, 10);
  const tomorrowForecast = (weather?.forecast || []).find((f) => f.date === tomorrowIso);
  // In evening mode, use tomorrow's forecast condition for the pool choice
  // (sunny/cloudy → outdoor pool; rainy/snowy → indoor pool).
  const effectiveCondition = evening && tomorrowForecast?.condition
    ? tomorrowForecast.condition
    : condition;
  const pickLabel = evening ? "Tomorrow's pick" : "Today's pick";
  // Tonight's pick: after 18:00, if any evening-friendly venues are still open
  // TODAY, surface one of those instead of jumping straight to tomorrow.
  const eveningPool = evening
    ? [...(outdoorPool?.items || []), ...(indoorPool?.items || [])]
        .filter((a, i, arr) => arr.findIndex((x) => x.slug === a.slug) === i)
        .filter((a) => (a.member_tier === 'gold' || a.member_tier === 'silver') && a.evening_friendly && isOpenToday(a, new Date()))
    : [];
  const tonightPick = eveningPool.length ? pickDaily(eveningPool, 1, 'tonight-pick')[0] : null;
  const Icon = WEATHER_ICON[condition] || Cloud;
  const dayCtx = dayContext();
  const seasonCtx = seasonContext();
  const timeCtx = timeContext();
  // Hero priority: pinned photo → starred favourites (rotate daily) → seasonal rotation
  const abs = (u) => (u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
  const dailyFav = heroFavs.length ? heroFavs[Math.floor(Date.now() / 86400000) % heroFavs.length] : null;
  const heroPick = heroOverride?.url ? heroOverride : dailyFav;
  const heroBg = heroPick ? abs(heroPick.url) : pickHeroImage();
  const heroTestId = heroOverride?.url ? 'hero-bg-custom' : (dailyFav ? 'hero-bg-favourite' : `hero-bg-${seasonCtx.key}-${timeCtx.key}`);
  const accent = condition === 'sunny' ? 'text-mustard' : condition === 'rainy' ? 'text-sky' : 'text-stone';

  // 3-town strip — other locations (excluding the currently-shown one)
  const stripLocations = county?.locations
    ? county.locations.filter((l) => l.location !== weather?.location).slice(0, 3)
    : [];

  // Sat + Sun outlook pills for the weekend events strip
  const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const fcByDate = Object.fromEntries((weather?.forecast || []).map((f) => [f.date, f]));
  const nowD = new Date();
  const dw = nowD.getDay();
  const satD = new Date(nowD); satD.setDate(nowD.getDate() + ((6 - dw) % 7));
  const sunD = new Date(satD); sunD.setDate(satD.getDate() + 1);
  const weekendOutlook = (dw === 0 ? [['Sun', nowD]] : [['Sat', satD], ['Sun', sunD]])
    .map(([lbl, d]) => ({ lbl, fc: fcByDate[isoLocal(d)] }))
    .filter((x) => x.fc && x.fc.temp_max != null);

  return (
    <div>
      <Helmet>
        <title>Visit Leicestershire · Things to Do, Days Out & Events | GoLeicestershire</title>
        <meta name="description" content="Planning to visit Leicester or Leicestershire? Explore the city, market towns, the National Forest, what's-on events and days out across the county — made for weekends."/>
        <meta property="og:title" content="Visit Leicestershire — GoLeicestershire"/>
        <meta property="og:description" content="Things to do, days out, events & short breaks across Leicester and Leicestershire."/>
        <meta property="og:type" content="website"/>
      </Helmet>
      {/* HERO — full-bleed, bold wordmark, animated headline */}
      <section className="relative bg-charcoal text-base overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroBg} alt={heroPick?.alt || ''} role="presentation" fetchpriority="high" decoding="async" data-testid={heroTestId} className="absolute inset-0 w-full h-full object-cover scale-105 motion-safe:animate-hero-zoom" />
          {/* Time-of-day overlay — warms the image at sunrise/sunset, cools it at night */}
          <div className="absolute inset-0 pointer-events-none mix-blend-soft-light" style={{ background: timeCtx.overlay }} aria-hidden />
          {/* Live weather effect — ONLY renders when it's actually raining/snowing in
              the destination right now (per /api/weather). No "snow-because-December" tricks. */}
          {weather?.condition === 'rainy' && (
            <div className="absolute inset-0 pointer-events-none overflow-hidden motion-safe:opacity-60" data-testid="hero-weather-rain" aria-hidden>
              <div className="absolute inset-0 hero-rain" />
              <div className="absolute inset-0 bg-gradient-to-b from-slate-900/15 via-transparent to-slate-900/10" />
            </div>
          )}
          {weather?.condition === 'snowy' && (
            <div className="absolute inset-0 pointer-events-none overflow-hidden motion-safe:opacity-90" data-testid="hero-weather-snow" aria-hidden>
              <div className="absolute inset-0 hero-snow" />
              <div className="absolute inset-0 hero-snow hero-snow--slow" />
              <div className="absolute inset-0 bg-gradient-to-b from-white/8 via-transparent to-white/4 mix-blend-screen" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/55 to-charcoal/15" />
          <div className="absolute inset-0 grain opacity-50" />
        </div>
        <div className="relative max-w-[1500px] mx-auto px-6 lg:px-12 pt-28 md:pt-32 pb-16 md:pb-24 min-h-[88svh] flex flex-col justify-end">
          {/* Top-right weather chip */}
          <div className="absolute top-6 right-6 lg:top-10 lg:right-12 z-10">
            <button
              onClick={cycleLocation}
              data-testid="hero-weather-cycle"
              title="Tap to see another town"
              className="backdrop-blur-md bg-charcoal/40 border border-base/20 hover:border-mustard/60 rounded-full px-4 py-2 inline-flex items-center gap-2.5 text-sm transition-colors group"
            >
              <Icon size={15} className={accent}/>
              <span className="text-base/95">{weather?.location || 'Leicestershire'}</span>
              <span className="text-base/60">·</span>
              <span className="font-semibold">{weather?.temperature ?? '–'}°</span>
              <span className="opacity-50 group-hover:opacity-100 text-xs">↺</span>
            </button>
          </div>

          {/* Giant wordmark — the user's first ask of Wave D */}
          <div className="mb-8 md:mb-10 motion-safe:animate-fade-up motion-safe:[animation-delay:0.05s] opacity-0" style={{ animationFillMode: 'forwards' }}>
            <div className="flex items-baseline gap-1.5 md:gap-3 pb-2 md:pb-4" data-testid="hero-wordmark">
              <span className="font-display text-[9vw] md:text-[9vw] lg:text-[7.5vw] leading-[0.95] tracking-tight text-base/95 drop-shadow-[0_4px_24px_rgba(0,0,0,0.45)]">go</span>
              <span className="flex flex-col">
                <span className="font-display text-[9vw] md:text-[9vw] lg:text-[7.5vw] leading-[0.95] tracking-tight text-mustard drop-shadow-[0_4px_24px_rgba(0,0,0,0.45)]">Leicestershire</span>
                <span className="mt-0.5 md:mt-1 pl-[0.4vw] uppercase font-medium text-base/55 text-[1.8vw] md:text-[1.4vw] lg:text-[1.05vw] tracking-[0.45em] drop-shadow-[0_2px_12px_rgba(0,0,0,0.5)]" data-testid="hero-strapline">
                  Stay&nbsp;&nbsp;Play&nbsp;&nbsp;Explore
                </span>
              </span>
            </div>
            <div className="mt-5 md:mt-8 inline-block backdrop-blur-sm bg-charcoal/40 px-3 py-1.5 rounded-full text-base/85 text-xs md:text-sm uppercase tracking-[0.3em] border border-base/10" data-testid="hero-context-chip">
              {timeCtx.greeting} · {seasonCtx.label}
              {weather?.condition === 'rainy' && <span className="ml-2 text-mustard/90">· Raining in {weather.location}</span>}
              {weather?.condition === 'snowy' && <span className="ml-2 text-base">· Snowing in {weather.location}</span>}
            </div>
          </div>

          {/* Animated headline — copy shifts per season + time of day */}
          <div className="max-w-3xl">
            <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl leading-[1.05] font-medium motion-safe:animate-fade-up motion-safe:[animation-delay:0.25s] opacity-0" style={{ animationFillMode: 'forwards' }} data-testid="hero-headline">
              {(() => {
                const k = `${seasonCtx.key}-${timeCtx.key}`;
                // Defaults live in lib/heroHeadlines; admins can override any
                // slot instantly from Admin → Homepage Copy (no redeploy).
                const lines = mergeHeadlines(heroCopy?.value?.lines);
                const opts = lines[k] || ['Castles by sunrise. Country lanes by lunch. A proper Leicestershire pub by dusk.'];
                // fresh line every visit (not just once a day)
                return opts[Math.floor(Math.random() * opts.length)];
              })()}
            </h1>
            <p className="mt-5 md:mt-7 text-base md:text-lg text-base/75 max-w-xl motion-safe:animate-fade-up motion-safe:[animation-delay:0.45s] opacity-0" style={{ animationFillMode: 'forwards' }} data-testid="hero-season-sub">
              {seasonCtx.sub} We turn your &ldquo;what shall we do {dayCtx.weekend ? 'this weekend' : 'this week'}?&rdquo; into a real plan. Pick a mood. Pick a postcode. We&apos;ll do the rest.
            </p>
            <div className="mt-7 md:mt-9 flex flex-wrap items-center gap-3 motion-safe:animate-fade-up motion-safe:[animation-delay:0.65s] opacity-0" style={{ animationFillMode: 'forwards' }}>
              <Link to="/plan" data-testid="hero-cta-plan" className="bg-mustard text-charcoal px-7 py-4 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-base hover:scale-[1.03] active:scale-95 transition-all">
                Plan my day <ArrowRight size={16}/>
              </Link>
              <Link to="/explore" data-testid="hero-cta-explore" className="border border-base/30 text-base px-7 py-4 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-base/10 transition-colors">
                Explore the county
              </Link>
              <Link to="/itinerary" data-testid="hero-cta-itinerary" className="hidden md:inline-flex text-base/70 hover:text-mustard text-sm font-semibold ml-2 items-center gap-1.5">
                Build an itinerary <ArrowUpRight size={13}/>
              </Link>
            </div>
          </div>

          {/* County strip — container always rendered (reserved height) so the
              async weather data doesn't shift the hero text when it arrives (CLS) */}
          <div className="mt-12 md:mt-16 min-h-[38px] flex flex-wrap gap-2 motion-safe:animate-fade-up motion-safe:[animation-delay:0.85s] opacity-0" style={{ animationFillMode: 'forwards' }} data-testid="county-weather-strip">
            {stripLocations.length > 0 && (
              <>
              <span className="text-base/40 text-xs uppercase tracking-widest mr-2 self-center">Across the county</span>
              {stripLocations.map((l) => (
                <Link
                  key={l.location}
                  to={`/explore?town=${encodeURIComponent(l.location)}`}
                  data-testid={`strip-loc-${l.location.toLowerCase().replace(/\s+/g, '-')}`}
                  className="text-xs bg-base/10 hover:bg-base/20 border border-base/15 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors"
                >
                  {l.location} <span className="text-base/60">{l.temperature}°</span>
                  <ArrowUpRight size={11} className="opacity-60"/>
                </Link>
              ))}
              </>
            )}
          </div>
        </div>
      </section>

      {/* TODAY'S PICKS STRIP — replaces the old dual right-column cards */}
      <section className="bg-base border-b border-charcoal/5 -mt-6 md:-mt-8 relative z-10">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-8 md:py-12">
          {/* min-h reserves the tile height before async data lands (CLS) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 min-h-[170px]">
            {(effectiveCondition === 'sunny' || effectiveCondition === 'cloudy') && (() => {
              // Only gold/silver members are eligible for the "Today's pick" slot,
              // AND we exclude venues that are out-of-season / closed on the
              // effective date (today, or tomorrow after 18:00) so we don't
              // surface (e.g.) Wistow Maze in February.
              const pool = (outdoorPool?.items || []).filter(
                (a) => (a.member_tier === 'gold' || a.member_tier === 'silver') && isOpenToday(a, targetDate)
              );
              const pick = tonightPick || pickDaily(pool, 1, `${dayLabel}-pick-outdoor-${effectiveCondition}`)[0];
              if (!pick) {
                return (
                  <Link to="/explore?outdoor=true" data-testid="weather-tile-outdoors" className="group bg-forest text-base rounded-3xl p-7 md:p-9 hover:bg-forest-deep transition-colors min-h-[170px] flex flex-col justify-between">
                    <div className="label text-mustard">{pickLabel}</div>
                    <div>
                      <div className="font-display text-2xl md:text-3xl leading-tight">Lace up. Head outdoors.</div>
                      <ArrowUpRight size={22} className="mt-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"/>
                    </div>
                  </Link>
                );
              }
              return (
                <Link
                  to={`/explore/${pick.slug}`}
                  data-testid="weather-tile-outdoors"
                  className="group relative bg-forest text-base rounded-3xl overflow-hidden hover:shadow-soft transition-all min-h-[170px] flex flex-col justify-between"
                >
                  {pick.hero_image && (
                    <img src={pick.hero_image} alt={pick.name} className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-40 transition-opacity" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-br from-forest/95 via-forest/80 to-forest-deep/95" />
                  <div className="relative p-7 md:p-9 flex flex-col justify-between h-full min-h-[170px]">
                    <div className="label text-mustard">{tonightPick ? "Tonight's pick · open late" : `${pickLabel} · outdoors`}</div>
                    <div>
                      <div className="font-display text-2xl md:text-3xl leading-tight">{pick.name}</div>
                      {pick.town && <div className="text-base/70 text-sm mt-1">{pick.town}</div>}
                      <ArrowUpRight size={22} className="mt-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"/>
                    </div>
                  </div>
                </Link>
              );
            })()}
            {(effectiveCondition === 'rainy' || effectiveCondition === 'snowy') && (() => {
              // Same pattern as the outdoor pick — gold/silver only, exclude
              // closed-on-effective-date venues, label flips today→tomorrow.
              const pool = (indoorPool?.items || []).filter(
                (a) => (a.member_tier === 'gold' || a.member_tier === 'silver') && isOpenToday(a, targetDate)
              );
              const pick = tonightPick || pickDaily(pool, 1, `${dayLabel}-pick-indoor-${effectiveCondition}`)[0];
              if (!pick) {
                return (
                  <Link to="/explore?indoor=true" data-testid="weather-tile-indoors" className="group bg-sky text-charcoal rounded-3xl p-7 md:p-9 hover:bg-sky/80 transition-colors min-h-[170px] flex flex-col justify-between">
                    <div className="label">Cosy plan</div>
                    <div>
                      <div className="font-display text-2xl md:text-3xl leading-tight">Museums, tea rooms, theatres.</div>
                      <ArrowUpRight size={22} className="mt-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"/>
                    </div>
                  </Link>
                );
              }
              return (
                <Link
                  to={`/explore/${pick.slug}`}
                  data-testid="weather-tile-indoors"
                  className="group relative bg-sky text-charcoal rounded-3xl overflow-hidden hover:shadow-soft transition-all min-h-[170px] flex flex-col justify-between"
                >
                  {pick.hero_image && (
                    <img src={pick.hero_image} alt={pick.name} className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-50 transition-opacity" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-br from-sky/90 via-sky/70 to-sky/95" />
                  <div className="relative p-7 md:p-9 flex flex-col justify-between h-full min-h-[170px]">
                    <div className="label">{tonightPick ? "Tonight's pick · open late" : evening ? "Tomorrow's cosy pick" : "Today's cosy pick"}</div>
                    <div>
                      <div className="font-display text-2xl md:text-3xl leading-tight">{pick.name}</div>
                      {pick.town && <div className="text-charcoal/70 text-sm mt-1">{pick.town}</div>}
                      <ArrowUpRight size={22} className="mt-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"/>
                    </div>
                  </div>
                </Link>
              );
            })()}
            <Link to="/packages" data-testid="weather-tile-packages" className="group bg-heritage text-base rounded-3xl p-7 md:p-9 hover:bg-heritage-deep transition-colors min-h-[170px] flex flex-col justify-between">
              <div className="label text-mustard">Stay &amp; save</div>
              <div>
                <div className="font-display text-2xl md:text-3xl leading-tight">Family breaks from £169.</div>
                <ArrowUpRight size={22} className="mt-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"/>
              </div>
            </Link>
            <Link to="/awards#finalists" data-testid="weather-tile-awards" className="group bg-charcoal text-base rounded-3xl p-7 md:p-9 hover:bg-charcoal/90 transition-colors min-h-[170px] flex flex-col justify-between border border-mustard/30">
              <div className="label text-mustard">Tourism Awards 2026</div>
              <div>
                <div className="font-display text-2xl md:text-3xl leading-tight">Meet the finalists.</div>
                <ArrowUpRight size={22} className="mt-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1 text-mustard"/>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* THIS WEEKEND STRIP */}
      {weekend?.items?.length > 0 && (
        <section className="bg-charcoal text-base py-16 md:py-20">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="flex flex-wrap items-end justify-between gap-4 md:gap-6 mb-8 md:mb-10">
              <div>
                <div className="label text-mustard mb-3 inline-flex items-center gap-3">
                  <span>{dayCtx.weekend ? 'This weekend' : "What's on this week"}</span>
                  {timeAgo(weekend.last_updated) && (
                    <span
                      data-testid="weekend-updated-pill"
                      className="normal-case tracking-normal text-[11px] text-base/55 bg-base/5 border border-base/15 px-2 py-0.5 rounded-full"
                    >
                      {timeAgo(weekend.last_updated)}
                    </span>
                  )}
                  {weekendOutlook.map(({ lbl, fc }) => {
                    const FIcon = WEATHER_ICON[fc.condition] || Cloud;
                    return (
                      <span
                        key={lbl}
                        data-testid={`weekend-forecast-${lbl.toLowerCase()}`}
                        title={fc.label}
                        className="normal-case tracking-normal text-[11px] text-base/70 bg-base/5 border border-base/15 px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                      >
                        <FIcon size={11} className="text-mustard"/> {lbl} {Math.round(fc.temp_max)}°
                      </span>
                    );
                  })}
                </div>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-none">{weekend.items.length} things happening</h2>
              </div>
              <Link to="/events" data-testid="weekend-see-all" className="text-base/80 hover:text-mustard inline-flex items-center gap-2 font-semibold text-sm md:text-base">See all events <ArrowRight size={16} /></Link>
            </div>
            {/* Mobile: swipe carousel */}
            <div className="md:hidden">
              <MobileSnapCarousel testid="weekend-carousel">
                {weekend.items.map((e) => (
                  <WeekendEventCard key={e.id} e={e} />
                ))}
              </MobileSnapCarousel>
            </div>
            {/* Desktop: horizontal scroll strip with arrow controls for non-touch users */}
            <ScrollStrip
              testid="weekend-desktop-scroll"
              theme="dark"
              className="hidden md:flex gap-6 overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-6 px-6 lg:-mx-12 lg:px-12 pb-2"
            >
              {weekend.items.map((e) => (
                <div key={e.id} className="snap-start shrink-0 w-[340px] lg:w-[380px]">
                  <WeekendEventCard e={e} />
                </div>
              ))}
              <div className="shrink-0 w-2" aria-hidden />
            </ScrollStrip>
            {/* Contextual signup: visitor is right here looking at the weekend
                list — invite them to get the same list every Friday by email. */}
            <div className="mt-10 md:mt-12 max-w-2xl" data-testid="weekend-signup">
              <div className="label text-mustard mb-2">Get this list every Friday</div>
              <p className="text-base/80 mb-4 text-base md:text-lg">
                We do this for you weekly. Pop in your email — one Friday digest, ~30 seconds to read, one click to unsubscribe.
              </p>
              <NewsletterSignup variant="dark" source="weekend-strip" buttonLabel="Get the Friday digest" />
            </div>
          </div>
        </section>
      )}

      {/* SCHOOL HOLIDAY IDEAS RAIL — only during Leicestershire school holidays */}
      {schoolHoliday && (holidayIdeas?.items || []).length > 0 && (() => {
        const pool = holidayIdeas.items.filter((a) => isOpenToday(a, new Date()));
        const picks = pickDaily(pool.length >= 6 ? pool : holidayIdeas.items, 8, 'school-holiday-rail');
        if (!picks.length) return null;
        return (
          <section className="bg-mustard/15 border-y border-mustard/30 py-16 md:py-20" data-testid="school-holiday-rail">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
              <div className="flex flex-wrap items-end justify-between gap-4 md:gap-6 mb-8 md:mb-10">
                <div className="max-w-2xl">
                  <div className="label text-heritage mb-3 inline-flex items-center gap-2"><Sparkles size={13}/> {schoolHoliday.label || 'School holiday'} ideas</div>
                  <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[0.95]">The kids are off. We&apos;ve got you.</h2>
                  <p className="text-muted mt-3 text-sm md:text-base">Family days out across the county — sorted for the school holidays.</p>
                </div>
                <Link to={`/events?from=${schoolHoliday.from}&to=${schoolHoliday.to}`} data-testid="school-holiday-see-events" className="text-heritage hover:text-charcoal inline-flex items-center gap-2 font-semibold text-sm md:text-base">
                  What&apos;s on in the holidays <ArrowRight size={16}/>
                </Link>
              </div>
              <div className="md:hidden">
                <MobileSnapCarousel testid="school-holiday-carousel">
                  {picks.map((a) => <AttractionCard key={a.slug} a={a} />)}
                </MobileSnapCarousel>
              </div>
              <ScrollStrip
                testid="school-holiday-scroll"
                className="hidden md:flex gap-6 overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-6 px-6 lg:-mx-12 lg:px-12 pb-2"
              >
                {picks.map((a) => (
                  <div key={a.slug} className="snap-start shrink-0 w-[320px] lg:w-[360px]">
                    <AttractionCard a={a} />
                  </div>
                ))}
                <div className="shrink-0 w-2" aria-hidden />
              </ScrollStrip>
            </div>
          </section>
        );
      })()}

      {/* FEATURED ATTRACTIONS — TETRIS GRID (desktop) / swipe carousel (mobile) */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap items-end justify-between gap-4 md:gap-6 mb-8 md:mb-12">
            <div className="max-w-2xl">
              <div className="label text-heritage mb-3">Standout days out</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl leading-[0.95]" data-testid="featured-heading">
                Six experiences that locals quietly brag about.
              </h2>
            </div>
            <Link to="/explore" data-testid="featured-see-all" className="inline-flex items-center gap-2 font-semibold hover:text-heritage text-sm md:text-base">All {featured?.total ?? ''} attractions <ArrowRight size={16} /></Link>
          </div>
          {(() => {
            const featuredDeduped = (featured?.items || []).slice(0, 6);
            return (
              <>
                {/* Mobile: swipe carousel */}
                <div className="md:hidden">
                  <MobileSnapCarousel testid="featured-carousel">
                    {featuredDeduped.map((a) => (
                      <AttractionCard key={a.id} a={a} size="md" />
                    ))}
                  </MobileSnapCarousel>
                </div>
                {/* Desktop: Tetris grid */}
                <div className="hidden md:grid grid-cols-12 gap-6 lg:gap-8">
                  {featuredDeduped.map((a, i) => (
                    <div key={a.id} className={
                      i === 0 ? 'col-span-7 row-span-2' :
                      i === 1 ? 'col-span-5' :
                      i === 2 ? 'col-span-5' :
                      'col-span-4'
                    }>
                      <AttractionCard a={a} size={i === 0 ? 'lg' : 'md'} />
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      </section>

      {/* WALKS STRIP — small outdoors nudge to county walking routes */}
      <section className="relative text-base py-12 md:py-20 overflow-hidden" data-testid="home-walks-strip">
        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/img/goleics/media/60ebe5fd3134482d8611c11f6c037c2a.jpg`}
          alt="" role="presentation" loading="lazy"
          className="absolute inset-0 w-full h-full object-cover" data-testid="home-walks-bg" />
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal/80 via-charcoal/40 to-charcoal/15" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
          <div className="flex items-start gap-4 flex-1">
            <div className="bg-base/15 backdrop-blur-sm rounded-full p-3 shrink-0"><Footprints size={22} className="text-mustard" /></div>
            <div>
              <h2 className="font-display text-xl md:text-2xl leading-tight">Leicestershire on foot</h2>
              <p className="text-base/85 text-sm md:text-[15px] mt-1 max-w-2xl drop-shadow-[0_1px_4px_rgba(0,0,0,0.5)]">
                Canal towpaths, country-park circuits and National Forest trails — dozens of free,
                tried-and-tested walking routes right across the county, with maps and directions.
              </p>
            </div>
          </div>
          <div className="shrink-0 md:text-right">
            <a href="https://www.gps-routes.co.uk/routes/home.nsf/county-walks/Leicestershire"
              target="_blank" rel="noopener noreferrer" data-testid="home-walks-link" data-outbound="website"
              className="inline-flex items-center gap-2 bg-mustard text-charcoal font-semibold px-6 py-3 rounded-full hover:bg-base transition-colors text-sm">
              Browse county walks <ArrowUpRight size={15} />
            </a>
            <p className="text-base/70 text-[11px] mt-2 drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)]">External site — routes by gps-routes.co.uk</p>
          </div>
        </div>
      </section>

      {/* STAYPLAYEXPLORE — HERO PROMO */}
      {packages?.items?.filter((p) => p.featured).length > 0 && (
        <section className="bg-heritage text-base py-16 md:py-24 relative overflow-hidden">
          <div className="absolute inset-0 grain" />
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center">
              <div className="lg:col-span-5">
                <div className="label text-mustard mb-3 md:mb-4">StayPlayExplore</div>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl leading-[0.95] mb-5 md:mb-6">
                  £169.<br />The whole family. <em className="not-italic text-mustard">Not per person.</em>
                </h2>
                <p className="text-base/85 text-base md:text-lg leading-relaxed max-w-md mb-6 md:mb-8">
                  Hotel, breakfast and three attraction tickets — for four people. The same price, every weekend, even in school holidays. (Yes, really.)
                </p>
                <a href="https://www.stayplayexplore.co.uk/" target="_blank" rel="noreferrer" data-testid="spe-cta-main" className="inline-flex items-center gap-2 bg-mustard text-charcoal font-semibold px-7 md:px-8 py-3.5 md:py-4 rounded-full hover:bg-base transition-all hover:scale-[1.03]">
                  Book a break <ArrowUpRight size={18} />
                </a>
              </div>
              <div className="lg:col-span-7">
                {/* Mobile: swipe carousel — every featured package, horizontally scrollable */}
                <div className="md:hidden">
                  <MobileSnapCarousel testid="packages-carousel">
                    {packages.items.filter((p) => p.featured).map((p) => (
                      <PackageCard key={p.id} p={p} hero={false} />
                    ))}
                  </MobileSnapCarousel>
                </div>
                {/* Desktop grid — first featured package is hero (full-width), rest in 2-col grid */}
                <div className="hidden md:grid grid-cols-2 gap-6">
                  {packages.items.filter((p) => p.featured).map((p, i) => (
                    <PackageCard key={p.id} p={p} hero={i === 0} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* THE PERFECT BREAK — ADULTS-ONLY PROMO */}
      {(() => {
        const pb = (packages?.items || []).find((p) => (p.slug || '').includes('perfect-break'));
        if (!pb) return null;
        return (
          <section className="bg-charcoal text-base py-16 md:py-20 relative overflow-hidden" data-testid="perfectbreak-home-section">
            <div className="absolute inset-0 grain" />
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center">
                <div className="lg:col-span-6">
                  <div className="label text-mustard mb-3 md:mb-4">The Perfect Break · Adults only</div>
                  <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-5 md:mb-6">
                    Leave the kids at home.<br /><em className="not-italic text-mustard">From £169 for two.</em>
                  </h2>
                  <p className="text-base/80 text-base md:text-lg leading-relaxed max-w-md mb-6 md:mb-8">
                    The grown-up sister to StayPlayExplore — a hotel stay, breakfast and up to three hand-picked
                    experiences for two, bundled at one fixed price. Battlefields, bell foundries, boutique bowling… no colouring-in menus.
                  </p>
                  <div className="flex flex-wrap items-center gap-4">
                    <a href="https://www.theperfectbreak.co.uk/" target="_blank" rel="noreferrer" data-testid="perfectbreak-cta-main" className="inline-flex items-center gap-2 bg-mustard text-charcoal font-semibold px-7 md:px-8 py-3.5 md:py-4 rounded-full hover:bg-base transition-all hover:scale-[1.03]">
                      Book an adults-only break <ArrowUpRight size={18} />
                    </a>
                    <Link to="/packages" data-testid="perfectbreak-cta-details" className="text-base/80 hover:text-mustard font-semibold inline-flex items-center gap-1.5">
                      See what&apos;s included
                    </Link>
                  </div>
                </div>
                <div className="lg:col-span-6">
                  <a href="https://www.theperfectbreak.co.uk/" target="_blank" rel="noreferrer" data-testid="perfectbreak-home-card" className="group relative block aspect-[16/10] rounded-[2rem] overflow-hidden">
                    {pb.hero_image && <img src={pb.hero_image} alt={pb.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/20 to-transparent" />
                    <span className="absolute top-5 left-5 bg-mustard text-charcoal text-xs font-semibold px-3 py-1.5 rounded-full">Adults only</span>
                    <div className="absolute bottom-0 left-0 p-6 md:p-8">
                      <h3 className="font-display text-2xl md:text-3xl leading-tight mb-1">{pb.title}</h3>
                      <p className="text-base/80 text-sm md:text-base">{pb.includes?.slice(0, 3).join(' · ')}</p>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* TOWNS */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap items-end justify-between gap-4 md:gap-6 mb-8 md:mb-12">
            <div className="max-w-xl">
              <div className="label text-heritage mb-3">Discover</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl leading-[0.95]">Explore Leicestershire. One city, nine towns, countless villages, endless stories.</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6 lg:gap-8">
            {(towns?.items || []).map((t) => (
              <Link key={t.id} to={`/towns/${t.slug}`} data-testid={`town-card-${t.slug}`} className="group relative aspect-[3/4] md:aspect-[4/5] rounded-2xl md:rounded-3xl overflow-hidden block">
                <img src={t.hero_image} alt={t.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/30 to-transparent" />
                <div className="absolute bottom-0 left-0 p-4 md:p-8 text-base">
                  <div className="label text-mustard mb-1 md:mb-2 text-[10px] md:text-xs">{t.slug === 'leicester' ? 'City' : 'Market town'}</div>
                  <h3 className={`font-display leading-tight ${t.name.length > 24 ? 'text-lg md:text-2xl' : 'text-xl md:text-3xl'}`}>{t.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* AI DIGEST — label + framing adapt to day-of-week so it never reads as
          "this weekend" content on a Tuesday */}
      {digest?.title && (() => {
        const dow = new Date().getDay(); // 0 Sun … 6 Sat
        const isWeekend = dow === 0 || dow === 5 || dow === 6;
        const isThursday = dow === 4;
        const label = isWeekend
          ? "This weekend's edit · Hot off the press"
          : isThursday
            ? 'The weekend is calling · Fresh from the editor\u2019s desk'
            : "This week's must-do list · Refreshed every Friday";
        return (
          <section className="py-16 md:py-24 bg-stone/50">
            <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
              <div className="bg-white rounded-[2rem] border border-charcoal/10 p-7 md:p-10 lg:p-16">
                <div className="label text-heritage mb-4 flex items-center gap-2" data-testid="digest-label"><Sparkles size={14} /> {label}</div>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight mb-4">{digest.title}</h2>
                {digest.intro && <p className="text-xl md:text-2xl text-charcoal/70 font-display leading-snug mb-6 md:mb-8">{digest.intro}</p>}
                <div className="prose prose-lg max-w-none text-muted whitespace-pre-wrap leading-relaxed">{digest.body}</div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* SOME OF LEICESTERSHIRE'S BEST — fixed 4-tile row that rotates daily.
          Same 4 for every visitor on a given day; new set when the UK day ticks. */}
      {members?.items?.length > 0 && (() => {
        const todaysPicks = pickDaily(members.items, 4, 'members-best');
        if (todaysPicks.length === 0) return null;
        return (
          <section className="py-14 md:py-20 border-t border-b border-charcoal/10">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12 mb-8 md:mb-10 flex items-end justify-between flex-wrap gap-4">
              <div>
                <div className="label text-heritage mb-2">Places not to be missed</div>
                <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl">Some of Leicestershire&apos;s best.</h2>
              </div>
              <Link to="/explore?featured_member=true" data-testid="members-see-all" className="font-semibold inline-flex items-center gap-2 hover:text-heritage text-sm md:text-base">See them all <ArrowRight size={16} /></Link>
            </div>
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6" data-testid="members-daily-grid">
                {todaysPicks.map((m) => (
                  <Link
                    key={m.slug}
                    to={`/explore/${m.slug}`}
                    data-testid={`member-card-${m.slug}`}
                    className="bg-white rounded-3xl overflow-hidden border border-charcoal/10 group hover:shadow-soft transition-all hover:-translate-y-0.5"
                  >
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={m.hero_image} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    <div className="p-5">
                      {m.town && <div className="label text-muted mb-2 flex items-center gap-1.5"><MapPin size={11}/> {m.town}</div>}
                      <h3 className="font-display text-lg leading-tight">{m.name}</h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        );
      })()}

      {/* FAQ — targets "visit Leicester" searches; mirrored as FAQPage JSON-LD above */}
      <FaqSection />

      {/* "Who we are" trust teaser — links into the full /about page. Sits
          right before the global footer so it's the last thing visitors see
          before the newsletter CTA. */}
      <AboutTeaser />

      {/* Newsletter CTA lives globally in <Footer /> (Layout.js) so every page
          gets it — no need to duplicate it here. */}
    </div>
  );
}

const FAQS = [
  { q: 'Is Leicester worth visiting?', a: "Absolutely. Leicester is one of England's oldest cities — home of the King Richard III story, the National Space Centre and a famous food scene — and it sits at the heart of Leicestershire's castles, market towns and countryside." },
  { q: 'What is there to do in Leicestershire?', a: 'Our guide covers 85+ venues and attractions: castles and stately homes, the National Forest, country parks, steam railways, market towns and award-winning family days out.', to: '/explore', toText: 'Browse all venues →' },
  { q: "What's on in Leicester and Leicestershire this weekend?", a: 'Our what\u2019s-on listings are updated all week — festivals, family events, heritage days and more, filterable by town and date.', to: '/events', toText: 'See the listings →' },
  { q: 'Which market towns should I visit in Leicestershire?', a: 'Melton Mowbray — home of the pork pie and Stilton cheese — Market Harborough, Loughborough, Ashby-de-la-Zouch and Hinckley each make a great day out.', to: '/towns', toText: 'Explore the towns →' },
  { q: 'Is Leicestershire good for family days out?', a: 'Yes — it\u2019s one of the Midlands\u2019 best counties for families, with zoos, hands-on science, steam trains, adventure play and plenty of free days out in country parks and the National Forest.' },
  { q: 'Where should I stay for a short break in Leicestershire?', a: 'From country-house hotels to market-town inns and farm stays, there\u2019s a bed for every budget.', to: '/accommodation', toText: 'Find a place to stay →' },
];

function FaqSection() {
  const [extras, setExtras] = useState([]);
  useEffect(() => {
    api.get('/site-settings/faq_extras')
      .then((r) => setExtras((r.data?.value?.items || []).filter((x) => x.q && x.a)))
      .catch(() => {});
  }, []);
  const all = [...FAQS, ...extras.map((x) => ({ q: x.q, a: x.a }))];
  return (
    <section className="py-16 md:py-24 bg-stone/40" data-testid="faq-section">
      <div className="max-w-[900px] mx-auto px-6 lg:px-12">
        <div className="label text-heritage mb-2">Planning a visit?</div>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl mb-8">Visiting Leicester &amp; Leicestershire — quick answers.</h2>
        <div className="space-y-3">
          {all.map((f, i) => (
            <details key={i} className="group bg-white border border-charcoal/10 rounded-2xl overflow-hidden" data-testid={`faq-item-${i}`}>
              <summary className="cursor-pointer list-none flex items-center justify-between gap-4 px-5 py-4 font-semibold text-sm md:text-[1rem] text-charcoal">
                {f.q}
                <ChevronDown size={16} className="shrink-0 transition-transform group-open:rotate-180" />
              </summary>
              <div className="px-5 pb-5 text-sm text-charcoal/80 leading-relaxed">
                {f.a}{' '}
                {f.to && <Link to={f.to} className="text-heritage font-semibold hover:underline">{f.toText}</Link>}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function WeekendEventCard({ e }) {  const dateStr = new Date(e.start_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  const inner = (
    <>
      <div className="aspect-[16/10] overflow-hidden">
        <SmartImage src={e.hero_image || eventPlaceholder(e.category)} fallback={eventPlaceholder(e.category)} alt={e.title} hoverZoom />
      </div>
      <div className="p-5 md:p-6">
        <div className="label text-mustard mb-2">{dateStr}</div>
        <h3 className="font-display text-xl md:text-2xl leading-tight mb-2 group-hover:text-mustard transition-colors">{e.title}</h3>
        <p className="text-base/70 text-sm mb-3 line-clamp-2">{e.summary}</p>
        <div className="flex items-center justify-between text-sm">
          <span className="text-base/60 flex items-center gap-1.5"><MapPin size={14} /> {e.town}</span>
          {e.url && <span className="text-mustard inline-flex items-center gap-1 font-semibold">Tickets <ArrowUpRight size={13}/></span>}
        </div>
      </div>
    </>
  );
  const cls = "group bg-charcoal border border-base/15 rounded-3xl overflow-hidden hover:border-mustard/60 transition-colors h-full block";
  // Cards always route to the in-app detail page so visitors land on a real
  // GoLeicestershire page first; from there they can click through to the
  // venue's external booking link.
  return (
    <Link to={`/events/${e.slug}`} className={cls} data-testid={`weekend-event-${e.slug}`}>
      {inner}
    </Link>
  );
}

function PackageCard({ p, hero }) {
  return (
    <a href={p.book_url} target="_blank" rel="noreferrer" data-testid={`spe-package-${p.slug}`}
      className={`group bg-base text-charcoal rounded-3xl overflow-hidden block hover:-translate-y-1 transition-transform h-full ${hero ? 'md:col-span-2' : ''}`}>
      <div className={`relative ${hero ? 'aspect-[16/9]' : 'aspect-[4/3]'} overflow-hidden`}>
        <img src={p.hero_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        {p.badge && <span className="absolute top-4 left-4 bg-charcoal text-base text-xs font-semibold px-3 py-1.5 rounded-full">{p.badge}</span>}
      </div>
      <div className="p-5 md:p-6">
        <div className="label text-heritage mb-2">{p.price_from}</div>
        <h3 className="font-display text-xl md:text-2xl leading-tight mb-2">{p.title}</h3>
        <p className="text-muted text-sm line-clamp-2">{p.subtitle}</p>
      </div>
    </a>
  );
}
